import React, {useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {useMutation, useQuery, useQueryClient} from 'react-query'
import {useAuth} from '../auth'
import {Loading} from '../apps/user-management/users-list/components/loading/Loading'
import {R_LIMITS} from './riskUtils'
import {
  computeEngagementPoints,
  computePayout,
  computeStreaks7,
  getEngagementBonus,
  getPeriodRange,
  getReferralMultiplier,
  getShareMultiplier,
  getWRMultiplier,
  toISODate,
  REFERRAL_MIN,
  SHARE_MIN,
  SHARE_CAP,
  SHARE_PER,
  REFERRAL_CAP,
  REFERRAL_PER,
} from './rewardUtils'
import {normalizeSocialScore, PLATFORM_LABELS, socialRankPeriodKey} from './socialRankTypes'
import {getInvitationList} from '_metronic/layout/core/requests'
import {apiURL} from '_metronic/helpers'

const CARD: React.CSSProperties = {
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: 14,
  padding: '20px 24px',
}

const SECTION_TITLE: React.CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 16,
  display: 'block',
}

type CryptoWallet = {
  id: string
  address: string
  chain: string
  label?: string | null
  is_verified?: boolean
}

type PayoutDestinationResponse = {
  id: string
  user_id: string
  method: 'okx_uid' | 'trc20' | 'metamask'
  destination: string
  network: string
  label?: string | null
  is_active: boolean
}

type TraderPayout = {
  id: string
  payout_month: string
  base_payment_usd: number
  performance_bonus_usd: number
  subscription_usd?: number
  total_usd: number
  payout_without_consistency_usd?: number
  calculation?: string | null
  method?: 'okx_uid' | 'trc20' | 'metamask' | null
  destination_snapshot?: string | null
  network?: string | null
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'manual_review'
  failure_reason?: string | null
  okx_transfer_id?: string | null
  tx_hash?: string | null
}

const LOCAL_PAYOUT_DESTINATIONS_KEY = 'bottomup-trader-payout-destinations'
const TRC20_ADDRESS_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/
const OKX_UID_RE = /^\d{6,20}$/
const SHOW_PAYOUT_SECTION = true
// Soft-launch gate for the Social Media Rank section. Keep false in prod until
// ingestion (OAuth or admin manual) is feeding real scores; flip to true to
// reveal the cards to all traders.
const SHOW_SOCIAL_RANK_SECTION = false

function formatUSD(value: number) {
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function shortAddress(address: string) {
  if (address.length <= 12) return address
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

function getApiErrorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as any
    return data?.message || data?.detail || data?.messages?.[0] || error.message || fallback
  }
  return error instanceof Error ? error.message : fallback
}

type PeriodKey =
  | 'today'
  | 'this_week'
  | 'this_month'
  | 'this_year'
  | 'yesterday'
  | 'last_week'
  | 'last_month'
  | 'last_year'

type PayoutMethod = 'metamask' | 'okx' | 'trc20'

const PAYOUT_METHODS: {value: PayoutMethod; label: string; description: string}[] = [
  {value: 'metamask', label: 'MetaMask', description: 'Connect and sign with browser wallet'},
  {value: 'okx', label: 'OKX UID', description: 'Receive by OKX account UID'},
  {value: 'trc20', label: 'TRC-20', description: 'Receive by Tron USDT address'},
]

function getPayoutMethod(wallet: CryptoWallet): PayoutMethod | null {
  const chain = wallet.chain.toLowerCase()
  const label = String(wallet.label || '').toLowerCase()
  if (chain === 'okx_uid' || chain === 'okx' || label.includes('okx')) return 'okx'
  if (chain === 'trc20' || chain === 'tron' || label.includes('trc')) return 'trc20'
  if (chain === 'metamask' || chain === 'ethereum' || label.includes('metamask')) return 'metamask'
  return null
}

function mapPayoutDestination(destination: PayoutDestinationResponse): CryptoWallet {
  return {
    id: destination.id,
    address: destination.destination,
    chain: destination.method,
    label: destination.label,
    is_verified: destination.is_active,
  }
}

function isNotFoundError(error: unknown) {
  return axios.isAxiosError(error) && error.response?.status === 404
}

function getLocalPayoutDestinations(userId?: string): CryptoWallet[] {
  if (!userId || typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(LOCAL_PAYOUT_DESTINATIONS_KEY)
    const items = raw ? (JSON.parse(raw) as Array<CryptoWallet & {user_id?: string}>) : []
    return items.filter((item) => item.user_id === userId)
  } catch {
    return []
  }
}

function saveLocalPayoutDestination(userId: string, wallet: CryptoWallet) {
  if (typeof window === 'undefined') return wallet
  let items: Array<CryptoWallet & {user_id?: string}> = []
  try {
    const raw = window.localStorage.getItem(LOCAL_PAYOUT_DESTINATIONS_KEY)
    items = raw ? (JSON.parse(raw) as Array<CryptoWallet & {user_id?: string}>) : []
  } catch {
    items = []
  }
  const method = getPayoutMethod(wallet)
  const nextItems = items.filter(
    (item) => !(item.user_id === userId && getPayoutMethod(item) === method)
  )
  const saved = {...wallet, user_id: userId}
  nextItems.unshift(saved)
  window.localStorage.setItem(LOCAL_PAYOUT_DESTINATIONS_KEY, JSON.stringify(nextItems))
  return saved
}

const PERIOD_OPTIONS: {value: PeriodKey; label: string}[] = [
  {value: 'today', label: 'Today'},
  {value: 'this_week', label: 'This Week'},
  {value: 'this_month', label: 'This Month'},
  {value: 'this_year', label: 'This Year'},
  {value: 'yesterday', label: 'Yesterday'},
  {value: 'last_week', label: 'Last Week'},
  {value: 'last_month', label: 'Last Month'},
  {value: 'last_year', label: 'Last Year'},
]

const toTargetDate = (period: PeriodKey) => {
  switch (period) {
    case 'today':
      return 'today'
    case 'yesterday':
      return 'yesterday'
    case 'this_week':
      return 'this_week'
    case 'last_week':
      return 'last_week'
    case 'this_month':
      return 'this_month'
    case 'last_month':
      return 'last_month'
    case 'this_year':
      return 'this_year'
    case 'last_year':
      return 'last_year'
    default:
      return 'this_month'
  }
}

export default function PerformanceDashboard() {
  const {currentUser} = useAuth()
  const queryClient = useQueryClient()
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodKey>('this_month')
  const [selectedPayoutMethod, setSelectedPayoutMethod] = useState<PayoutMethod>('metamask')
  const [selectedPayoutWalletId, setSelectedPayoutWalletId] = useState('')
  const [trc20Address, setTrc20Address] = useState('')
  const [okxUid, setOkxUid] = useState('')
  const [payoutMessage, setPayoutMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)
  const targetDate = toTargetDate(selectedPeriod)
  const selectedPeriodLabel =
    PERIOD_OPTIONS.find((opt) => opt.value === selectedPeriod)?.label ?? 'Selected Period'

  const {data: leaderboardRows, isLoading: isLeaderboardLoading} = useQuery(
    ['performance-futures-leaderboard', currentUser?.name, targetDate],
    async () => {
      const res = await axios.get(apiURL('/analytic/futures-leaderboard'), {
        params: {
          analyst_name: currentUser?.name,
          target_date: targetDate,
          skip: 0,
          limit: 1,
        },
      })
      return res.data?.data ?? []
    },
    {enabled: Boolean(currentUser?.name), refetchOnWindowFocus: false}
  )

  const {data: latestRows, isLoading: isLatestLoading} = useQuery(
    ['performance-futures-latest', currentUser?.name, targetDate],
    async () => {
      const res = await axios.get(apiURL('/analytic/futures-latest-setup'), {
        params: {
          analyst_name: currentUser?.name,
          target_date: targetDate,
          skip: 0,
          limit: 25,
        },
      })
      return res.data?.data ?? []
    },
    {enabled: Boolean(currentUser?.name), refetchOnWindowFocus: false}
  )

  const {data: netRWindows} = useQuery(
    ['performance-net-r-windows', currentUser?.id],
    async () => {
      const res = await axios.get(apiURL('/analytics/trader', currentUser?.id, 'net-r/windows'), {
        params: {category: 'futures'},
      })
      return res.data?.data ?? {}
    },
    {enabled: Boolean(currentUser?.id), refetchOnWindowFocus: false}
  )

  const {data: rankingData} = useQuery(
    ['performance-ranking', currentUser?.name, targetDate],
    async () => {
      const res = await axios.get(apiURL('/analytic/futures-leaderboard'), {
        params: {
          target_date: targetDate,
          skip: 0,
          limit: 100,
          order_by: 'net_r',
          order_direction: 'desc',
        },
      })
      const rows: any[] = res.data?.data ?? []
      const total = res.data?.total_count ?? rows.length
      const idx = rows.findIndex((r: any) => r.analysts === currentUser?.name)
      return {rank: idx >= 0 ? idx + 1 : null, totalTraders: total}
    },
    {enabled: Boolean(currentUser?.name), refetchOnWindowFocus: false}
  )

  const {data: periodLeaderboard} = useQuery(
    ['performance-period-lb', currentUser?.name, targetDate],
    async () => {
      const res = await axios.get(apiURL('/analytic/futures-leaderboard'), {
        params: {analyst_name: currentUser?.name, target_date: targetDate, skip: 0, limit: 1},
      })
      return res.data?.data?.[0] ?? null
    },
    {enabled: Boolean(currentUser?.name), refetchOnWindowFocus: false}
  )

  const {data: periodLatest} = useQuery(
    ['performance-period-latest', currentUser?.name, targetDate],
    async () => {
      const all: any[] = []
      let skip = 0
      const batchSize = 100
      while (true) {
        const res = await axios.get(apiURL('/analytic/futures-latest-setup'), {
          params: {
            analyst_name: currentUser?.name,
            target_date: targetDate,
            skip,
            limit: batchSize,
          },
        })
        const batch = res.data?.data ?? []
        all.push(...batch)
        if (batch.length < batchSize) break
        skip += batchSize
      }
      return all
    },
    {enabled: Boolean(currentUser?.name), refetchOnWindowFocus: false}
  )

  const {data: periodInvitations} = useQuery(
    ['performance-period-invitations', currentUser?.id, targetDate],
    async () => {
      const {start, end} = getPeriodRange(selectedPeriod)
      const all: any[] = []
      let skip = 0
      const batchSize = 100
      while (true) {
        const res = await getInvitationList({
          from_date: toISODate(start),
          to_date: toISODate(end),
          skip,
          limit: batchSize,
        })
        const batch = res.data?.data ?? []
        all.push(...batch)
        if (batch.length < batchSize || all.length >= 1000) break
        skip += batchSize
      }
      return all
    },
    {enabled: Boolean(currentUser?.id), refetchOnWindowFocus: false}
  )

  const socialRankPeriod = SHOW_SOCIAL_RANK_SECTION ? socialRankPeriodKey(selectedPeriod) : null
  const {data: socialScore, isLoading: isSocialScoreLoading} = useQuery(
    ['performance-social-score', currentUser?.id, socialRankPeriod],
    async () => {
      const res = await axios.get(apiURL('/social-rank/me/score'), {
        params: {period: socialRankPeriod},
      })
      const raw = (res.data as {data?: Record<string, unknown>})?.data ?? res.data
      return normalizeSocialScore(raw as Record<string, unknown>)
    },
    {enabled: Boolean(currentUser?.id) && Boolean(socialRankPeriod), refetchOnWindowFocus: false}
  )

  const {data: walletsData, isLoading: isWalletsLoading} = useQuery(
    ['performance-payout-destinations', currentUser?.id],
    async () => {
      try {
        const res = await axios.get<PayoutDestinationResponse[]>(
          apiURL('/trader/payout-destinations')
        )
        const items = (res.data ?? []).map(mapPayoutDestination)
        return {items, total: items.length}
      } catch (error) {
        if (!isNotFoundError(error)) throw error
        const items = getLocalPayoutDestinations(currentUser?.id)
        return {items, total: items.length}
      }
    },
    {enabled: SHOW_PAYOUT_SECTION && Boolean(currentUser?.id), refetchOnWindowFocus: false}
  )

  const officialPayoutPeriod =
    selectedPeriod === 'this_month' || selectedPeriod === 'last_month' ? selectedPeriod : null

  const {data: currentPayout} = useQuery(
    ['performance-current-payout', currentUser?.id, officialPayoutPeriod],
    async () => {
      if (!officialPayoutPeriod) return null
      try {
        const res = await axios.get<{items?: TraderPayout[]}>(apiURL('/trader/payouts/me'), {
          params: {period: officialPayoutPeriod},
        })
        return res.data.items?.[0] ?? null
      } catch (error) {
        if (!isNotFoundError(error)) throw error
        return null
      }
    },
    {
      enabled: SHOW_PAYOUT_SECTION && Boolean(currentUser?.id) && Boolean(officialPayoutPeriod),
      refetchOnWindowFocus: false,
    }
  )

  const connectWalletMutation = useMutation(
    async (payload: {
      address: string
      chain: string
      label?: string
      meta_data?: Record<string, unknown>
    }) => {
      const method =
        payload.chain === 'okx'
          ? 'okx_uid'
          : payload.chain === 'tron'
          ? 'trc20'
          : payload.chain === 'ethereum'
          ? 'metamask'
          : payload.chain
      try {
        const res = await axios.post<PayoutDestinationResponse>(
          apiURL('/trader/payout-destination'),
          {
            method,
            destination: payload.address,
            label: payload.label,
            meta_data: payload.meta_data,
          }
        )
        return mapPayoutDestination(res.data)
      } catch (error) {
        if (!isNotFoundError(error) || !currentUser?.id) throw error
        return saveLocalPayoutDestination(currentUser.id, {
          id: `${method}-${payload.address}`,
          address: payload.address,
          chain: method,
          label: payload.label,
          is_verified: true,
        })
      }
    },
    {
      onSuccess: (wallet) => {
        setSelectedPayoutWalletId(wallet.id)
        queryClient.invalidateQueries(['performance-payout-destinations'])
        queryClient.invalidateQueries(['performance-current-payout'])
        setPayoutMessage({
          type: 'success',
          text: `${wallet.label || wallet.chain} payout destination saved.`,
        })
      },
      onError: (error) => {
        setPayoutMessage({
          type: 'error',
          text: getApiErrorMessage(error, 'Failed to save payout destination'),
        })
      },
    }
  )

  useEffect(() => {
    const wallets = walletsData?.items ?? []
    const currentWallet = wallets.find((wallet) => wallet.id === selectedPayoutWalletId)
    if (currentWallet && getPayoutMethod(currentWallet) === selectedPayoutMethod) return
    const firstMethodWallet = wallets.find(
      (wallet) => getPayoutMethod(wallet) === selectedPayoutMethod
    )
    setSelectedPayoutWalletId(firstMethodWallet?.id ?? '')
  }, [selectedPayoutMethod, selectedPayoutWalletId, walletsData])

  const metrics = useMemo(() => {
    const lb = Array.isArray(leaderboardRows) && leaderboardRows.length ? leaderboardRows[0] : null
    const recentClosed = (Array.isArray(latestRows) ? latestRows : [])
      .filter((s: any) =>
        ['success', 'stopped', 'closed'].includes(String(s.status || '').toLowerCase())
      )
      .map((s: any, idx: number) => ({
        id: `${s.coin_name ?? 'setup'}-${s.target_date ?? idx}-${idx}`,
        status: String(s.status || '').toLowerCase(),
        coin_name: s.coin_name ?? '-',
        coin_icon: s.image ?? '',
        category: 'futures',
        position: s.position ?? null,
        realizedR: Number(s.r ?? 0),
        _eventDate: s.target_date ? new Date(s.target_date) : null,
      }))

    return {
      total: Number(lb?.success ?? 0) + Number(lb?.stopped ?? 0) || 0,
      wins: Number(lb?.success ?? 0),
      losses: Number(lb?.stopped ?? 0),
      winRate: Number(lb?.winrate ?? 0),
      netR: Number(lb?.net_r ?? 0),
      totalRGained: Number(lb?.r ?? 0),
      totalRLost: Math.max(0, Number(lb?.r ?? 0) - Number(lb?.net_r ?? 0)),
      byStatus: {
        success: Number(lb?.success ?? 0),
        stopped: Number(lb?.stopped ?? 0),
        cancelled: 0,
      },
      recentClosed,
      dailyR: Number(netRWindows?.last_24h ?? 0),
      weeklyR: Number(netRWindows?.this_week ?? 0),
      monthlyR: Number(netRWindows?.this_month ?? 0),
    }
  }, [leaderboardRows, latestRows, netRWindows])

  if (isLeaderboardLoading || isLatestLoading) return <Loading className='py-20' />
  if (!metrics) return null

  const statCards = [
    {
      label: 'Win Rate',
      value: `${metrics.winRate.toFixed(1)}%`,
      color: metrics.winRate >= 50 ? '#22C55E' : '#E84040',
      sub: `${metrics.wins}W / ${metrics.losses}L`,
    },
    {
      label: 'Net R',
      value: (metrics.netR >= 0 ? '+' : '') + metrics.netR.toFixed(2) + 'R',
      color: metrics.netR >= 0 ? '#22C55E' : '#E84040',
      sub: `+${metrics.totalRGained.toFixed(2)}R / -${metrics.totalRLost.toFixed(2)}R`,
    },
    {
      label: 'Period Rank',
      value: rankingData?.rank ? `#${rankingData.rank}` : '—',
      color: '#818cf8',
      sub: rankingData?.totalTraders ? `out of ${rankingData.totalTraders} traders` : '',
    },
    {
      label: 'Total Setups',
      value: String(metrics.total),
      color: '#94a3b8',
      sub: `${metrics.wins + metrics.losses} closed`,
    },
  ]

  const statusBar = [
    {label: 'Success', count: metrics.byStatus.success, color: '#22C55E'},
    {label: 'Stopped', count: metrics.byStatus.stopped, color: '#E84040'},
    {label: 'Cancelled', count: metrics.byStatus.cancelled, color: '#475569'},
  ].filter((s) => s.count > 0)

  const totalStatusCount = statusBar.reduce((s, i) => s + i.count, 0)

  const rPeriods = [
    {label: 'Daily', value: metrics.dailyR, limit: R_LIMITS.daily},
    {label: 'Weekly', value: metrics.weeklyR, limit: R_LIMITS.weekly},
    {label: 'Monthly', value: metrics.monthlyR, limit: R_LIMITS.monthly},
  ]

  const rLimitRules = [
    {period: 'Daily', limit: '-3R', consequence: 'Cannot trade for 24 hours', color: '#F59E0B'},
    {
      period: 'Weekly',
      limit: '-7.5R',
      consequence: 'Cannot trade for 1 week / 168 hours',
      color: '#F97316',
    },
    {
      period: 'Monthly',
      limit: '-10R',
      consequence: 'Cannot trade for the following month',
      color: '#E84040',
    },
  ]

  // --- Earnings & Consistency (selected period) ---
  const pLb = periodLeaderboard as any
  const periodNetR = Number(pLb?.net_r ?? 0)
  const periodWR = Number(pLb?.winrate ?? 0)
  const periodTrades = Number(pLb?.success ?? 0) + Number(pLb?.stopped ?? 0) || 0

  const earningsChecks = [
    {label: 'Minimum 2R profit', pass: periodNetR >= 2, value: `${periodNetR.toFixed(2)}R`},
    {label: 'Minimum 33% win rate', pass: periodWR >= 33, value: `${periodWR.toFixed(0)}%`},
    {label: 'At least 3 trades', pass: periodTrades >= 3, value: `${periodTrades}`},
  ]

  // --- Consistency Rules (from selected period latest setups) ---
  const pLatest = Array.isArray(periodLatest) ? periodLatest : []
  const closedPeriod = pLatest.filter((s: any) =>
    ['success', 'stopped', 'closed'].includes(String(s.status || '').toLowerCase())
  )

  const dayMap = new Map<string, {r: number; trades: any[]}>()
  for (const s of closedPeriod) {
    const dateStr = s.target_date || s.close_date || s.updated_at || s.created_at
    if (!dateStr) continue
    const day = String(dateStr).slice(0, 10)
    const current = dayMap.get(day) ?? {r: 0, trades: []}
    current.r += Number(s.r ?? s.pnl ?? 0)
    current.trades.push(s)
    dayMap.set(day, current)
  }

  const dailyBreakdown = Array.from(dayMap.entries())
    .map(([day, value]) => ({
      day,
      r: value.r,
      tradeCount: value.trades.length,
    }))
    .sort((a, b) => b.day.localeCompare(a.day))
  const uniqueTradingDays = dayMap.size
  const dayValues = dailyBreakdown.map((item) => item.r)
  const profitableDayValues = dayValues.filter((r) => r > 0)
  const profitableDays = dayValues.filter((r) => r >= 0.5).length

  const totalProfit = profitableDayValues.reduce((a, b) => a + b, 0)
  const maxProfitDay =
    profitableDayValues.length > 0 ? Math.max.apply(null, profitableDayValues) : 0
  const consistencyPct = totalProfit > 0 ? (maxProfitDay / totalProfit) * 100 : 0
  const consistencyLimit = 35

  const consistencyChecks = [
    {
      label: `Consistency Rule (single day cannot exceed ${consistencyLimit}% of total profit)`,
      pass: totalProfit <= 0 ? null : consistencyPct <= consistencyLimit,
      value:
        totalProfit > 0
          ? `${consistencyPct.toFixed(0)}% (best day: ${maxProfitDay.toFixed(2)}R)`
          : 'N/A',
    },
    {
      label: 'Minimum Trading Days (10 days with open trades)',
      pass: uniqueTradingDays >= 10,
      value: `${uniqueTradingDays}/10`,
    },
    {
      label: 'Minimum Profitable Days (5 days with net +0.5R)',
      pass: profitableDays >= 5,
      value: `${profitableDays}/5`,
    },
    {label: 'Minimum Trade Duration (5 minutes)', pass: null, value: 'Server-side'},
  ]
  const consistencyTradeCount = closedPeriod.length
  const earningsEligible = earningsChecks.every((c) => c.pass)
  const consistencyEligible = consistencyChecks.every((c) => c.pass !== false)
  const isEligible = earningsEligible && consistencyEligible

  const wrMultiplier = getWRMultiplier(periodWR)
  const bonusA = 30 * periodNetR
  const estimatedBonus = isEligible ? Math.max(0, bonusA * wrMultiplier) : 0
  const minimumPayment = isEligible ? 100 : 0
  const totalPeriodEarnings = minimumPayment + estimatedBonus

  // --- Rewards & Growth (selected period) ---
  // Shares = setups published in the period (open or closed).
  const periodSetups = Array.isArray(periodLatest) ? periodLatest : []
  const sharesCount = periodSetups.length

  // Active referrals = invitations registered in the period that are still
  // active (not cancelled, not expired).
  const periodInvitationList = Array.isArray(periodInvitations) ? periodInvitations : []
  const activeReferrals = periodInvitationList.filter(
    (inv: any) => !inv?.is_cancelled && !inv?.is_expired
  ).length

  // Engagement signals derived from setup activity. Follower interactions are
  // not tracked yet (needs backend) and stay at 0 for now.
  const activeDateSet = new Set<string>()
  for (const s of periodSetups) {
    const d =
      (s as any).target_date ||
      (s as any).created_at ||
      (s as any).close_date ||
      (s as any).updated_at
    if (d) activeDateSet.add(String(d).slice(0, 10))
  }
  const activeDaysCount = activeDateSet.size
  const takeProfitSetups = closedPeriod.filter(
    (s: any) => String(s.status || '').toLowerCase() === 'success'
  ).length
  const streaks7 = computeStreaks7(Array.from(activeDateSet))
  const engagementBreakdown = computeEngagementPoints({
    activeDays: activeDaysCount,
    shares: sharesCount,
    takeProfits: takeProfitSetups,
    interactions: 0,
    streaks7,
  })
  const engagementBonus = getEngagementBonus(engagementBreakdown.total, isEligible)

  const shareMultiplier = getShareMultiplier(sharesCount)
  const referralMultiplier = getReferralMultiplier(activeReferrals)
  const rewardPayout = computePayout({
    eligible: isEligible,
    netR: periodNetR,
    wrMultiplier,
    shareMultiplier,
    referralMultiplier,
    engagementBonus,
  })
  const bonusMultiplierTable = [
    {range: '33 - 35%', mult: '0.75'},
    {range: '35 - 37%', mult: '0.90'},
    {range: '37 - 40%', mult: '0.95'},
    {range: '40 - 45%', mult: '1.00'},
    {range: '45 - 50%', mult: '1.10'},
    {range: '50 - 67%', mult: '1.25'},
    {range: '67%+', mult: '1.35'},
  ]

  const payoutWallets = walletsData?.items ?? []
  const methodWallets = payoutWallets.filter(
    (wallet) => getPayoutMethod(wallet) === selectedPayoutMethod
  )
  const selectedWallet = methodWallets.find((wallet) => wallet.id === selectedPayoutWalletId)
  const isCurrentPayoutPeriod = selectedPeriod === 'this_month'
  const suggestedPayoutUsd = Math.floor(totalPeriodEarnings)
  const payoutUsd =
    isCurrentPayoutPeriod && currentPayout ? currentPayout.total_usd : totalPeriodEarnings
  const payoutStatus = isCurrentPayoutPeriod ? currentPayout?.status ?? 'pending' : 'calculated'
  const payoutStatusLabel = payoutStatus.replace('_', ' ')
  const canRequestPayout =
    isCurrentPayoutPeriod && isEligible && totalPeriodEarnings > 0 && Boolean(selectedWallet)

  const handleMetaMaskConnect = async () => {
    setPayoutMessage(null)
    const eth = typeof window !== 'undefined' ? (window as any).ethereum : undefined
    if (!eth?.request) {
      setPayoutMessage({type: 'error', text: 'MetaMask is not installed in this browser.'})
      return
    }

    try {
      const accounts = (await eth.request({method: 'eth_requestAccounts'})) as string[]
      const address = accounts?.[0]
      if (!address) {
        setPayoutMessage({type: 'error', text: 'MetaMask did not expose an account.'})
        return
      }
      const existingWallet = payoutWallets.find(
        (wallet) => wallet.address.toLowerCase() === address.toLowerCase()
      )
      if (existingWallet) {
        setSelectedPayoutWalletId(existingWallet.id)
        setPayoutMessage({type: 'success', text: 'MetaMask wallet is already selected.'})
        return
      }
      const nonce = `${Date.now()}-${Math.random().toString(16).slice(2)}`
      const message = [
        `${window.location.host} wants to connect this payout wallet:`,
        address,
        '',
        'This signature links the wallet to your BottomUp trader account.',
        `Nonce: ${nonce}`,
      ].join('\n')
      const signature = (await eth.request({
        method: 'personal_sign',
        params: [message, address],
      })) as string

      connectWalletMutation.mutate({
        address: address.toLowerCase(),
        chain: 'ethereum',
        label: 'MetaMask',
        meta_data: {connector: 'metamask', siwe_message: message, signature, nonce},
      })
    } catch (error) {
      setPayoutMessage({
        type: 'error',
        text: getApiErrorMessage(error, 'MetaMask connection was cancelled or failed.'),
      })
    }
  }

  const handleConnectTrc20 = () => {
    setPayoutMessage(null)
    const normalized = trc20Address.trim()
    if (!TRC20_ADDRESS_RE.test(normalized)) {
      setPayoutMessage({type: 'error', text: 'Enter a valid TRC-20 address starting with T.'})
      return
    }
    const existingWallet = payoutWallets.find((wallet) => wallet.address === normalized)
    if (existingWallet) {
      setSelectedPayoutWalletId(existingWallet.id)
      setPayoutMessage({type: 'success', text: 'TRC-20 wallet is already selected.'})
      return
    }

    connectWalletMutation.mutate({
      address: normalized,
      chain: 'tron',
      label: 'TRC-20',
      meta_data: {network: 'TRC-20', source: 'performance-payout'},
    })
  }

  const handleConnectOkxUid = () => {
    setPayoutMessage(null)
    const normalized = okxUid.trim()
    if (!OKX_UID_RE.test(normalized)) {
      setPayoutMessage({type: 'error', text: 'Enter a valid OKX UID with 6 to 20 digits.'})
      return
    }
    const existingWallet = payoutWallets.find(
      (wallet) => getPayoutMethod(wallet) === 'okx' && wallet.address === normalized
    )
    if (existingWallet) {
      setSelectedPayoutWalletId(existingWallet.id)
      setPayoutMessage({type: 'success', text: 'OKX UID is already selected.'})
      return
    }

    connectWalletMutation.mutate({
      address: normalized,
      chain: 'okx',
      label: 'OKX UID',
      meta_data: {network: 'OKX', source: 'performance-payout', payout_type: 'okx_uid'},
    })
  }

  const handleRequestPayout = () => {
    setPayoutMessage(null)
    if (!canRequestPayout || !selectedWallet) {
      setPayoutMessage({type: 'error', text: 'Save an eligible payout destination first.'})
      return
    }
    queryClient.invalidateQueries(['performance-current-payout'])
    setPayoutMessage({
      type: 'success',
      text: 'Your payout destination is saved. The backend will automatically send eligible payouts on the 20th.',
    })
  }

  const tradingRules = [
    {
      label: 'Min stop distance',
      desc: '0.25% for limit orders and 0.4% for market orders. Trades below these limits are not permitted.',
      icon: '📏',
    },
    {
      label: 'Min profit threshold',
      desc: 'A trade must close with at least 0.4R profit to count as a successful entry.',
      icon: '📊',
    },
    {
      label: 'Loss threshold',
      desc: 'Any trade with a loss exceeding -0.1R is considered a stop.',
      icon: '🛑',
    },
    {
      label: 'Active trade risk limit',
      desc: 'Risk cannot be increased above 1.5R while a trade is active. Stop-loss can only be modified up to 1.5R risk.',
      icon: '⚠',
    },
    {
      label: 'Repetitive trades',
      desc: 'Highly similar or same-direction repetitive trades are blocked to protect user assets and prevent bypassing rules.',
      icon: '🔁',
    },
    {
      label: 'Notification spam prevention',
      desc: 'A single trade can be updated at most 5 times per minute.',
      icon: '🔔',
    },
  ]
  const failedEarningChecks = earningsChecks.filter((check) => check.pass === false)
  const failedConsistencyChecks = consistencyChecks.filter((check) => check.pass === false)
  const notEligibleReasons = [...failedEarningChecks, ...failedConsistencyChecks]
  const closedPeriodDetails = [...closedPeriod]
    .sort((a: any, b: any) => {
      const aDate = new Date(
        a.target_date || a.close_date || a.updated_at || a.created_at || 0
      ).getTime()
      const bDate = new Date(
        b.target_date || b.close_date || b.updated_at || b.created_at || 0
      ).getTime()
      return bDate - aDate
    })
    .slice(0, 50)
  const formatTradeDate = (value?: string | null) => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric'})
  }

  return (
    <div style={{maxWidth: 1000, margin: '0 auto', padding: '0 12px 40px'}}>
      <div style={{marginBottom: 24}}>
        <h1 style={{color: 'var(--text-primary)', fontSize: 22, fontWeight: 700, margin: 0}}>
          Performance
        </h1>
        <p style={{color: 'var(--text-muted)', fontSize: 13, margin: '4px 0 0'}}>
          {metrics.total} total setup{metrics.total !== 1 ? 's' : ''}
        </p>
        <p style={{color: 'var(--text-faint)', fontSize: 12, margin: '6px 0 0'}}>
          Selected Period: {selectedPeriodLabel}
        </p>
      </div>

      <div
        style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 16, flexWrap: 'wrap'}}
      >
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value as PeriodKey)}
          style={{
            background: 'var(--elevated-bg)',
            border: '1px solid var(--card-border)',
            color: 'var(--text-secondary)',
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 12,
            width: 'min(100%, 220px)',
            minWidth: 170,
          }}
        >
          {PERIOD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Stat cards */}
      <div
        style={{gap: 12, marginBottom: 16}}
        className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
      >
        {statCards.map(({label, value, color, sub}) => (
          <div key={label} style={CARD}>
            <span
              style={{
                color: 'var(--text-muted)',
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                display: 'block',
                marginBottom: 8,
              }}
            >
              {label}
            </span>
            <span style={{color, fontSize: 26, fontWeight: 700, display: 'block', lineHeight: 1}}>
              {value}
            </span>
            <span
              style={{color: 'var(--text-faint)', fontSize: 12, marginTop: 6, display: 'block'}}
            >
              {sub}
            </span>
          </div>
        ))}
      </div>

      {/* Your R Status + R-Limit Rules */}
      <div style={{gap: 12, marginBottom: 12}} className='grid grid-cols-1 lg:grid-cols-2'>
        {/* Your R Status */}
        <div style={CARD}>
          <span style={SECTION_TITLE}>Your R Status</span>
          <div style={{display: 'flex', flexDirection: 'column', gap: 0}}>
            {rPeriods.map(({label, value, limit}, idx) => {
              const hasActivity = value !== 0
              const pct = Math.min(Math.abs(value / limit) * 100, 100)
              const isNegative = value < 0
              const barColor = isNegative
                ? value <= limit * 0.8
                  ? '#E84040'
                  : '#F59E0B'
                : '#22C55E'
              return (
                <div
                  key={label}
                  style={{
                    padding: '10px 0',
                    borderBottom: idx < rPeriods.length - 1 ? '1px solid var(--divider)' : 'none',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: hasActivity ? 8 : 0,
                    }}
                  >
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                      {hasActivity && (
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            background: barColor,
                            display: 'inline-block',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <span
                        style={{
                          color: 'var(--text-secondary)',
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        {label}
                      </span>
                    </div>
                    <span
                      style={{
                        color: hasActivity ? barColor : 'var(--text-faint)',
                        fontSize: 13,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {hasActivity ? `${value > 0 ? '+' : ''}${value.toFixed(2)}R` : '—'} /{' '}
                      <span style={{color: 'var(--text-muted)', fontWeight: 500}}>{limit}R</span>
                    </span>
                  </div>
                  {hasActivity && (
                    <div
                      style={{
                        height: 4,
                        background: 'var(--elevated-bg)',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${pct}%`,
                          background: barColor,
                          borderRadius: 4,
                          transition: 'width 0.4s',
                        }}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* R-Limit Rules */}
        <div style={CARD}>
          <span style={SECTION_TITLE}>R-Limit Rules</span>
          <p
            style={{color: 'var(--text-muted)', fontSize: 12, margin: '0 0 14px', lineHeight: 1.5}}
          >
            Analysts who exceed performance limits cannot share new setups for a certain period.
          </p>
          <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
            {rLimitRules.map(({period, limit, consequence, color}) => (
              <div
                key={period}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 8,
                  padding: '10px 14px',
                  background: 'var(--elevated-bg)',
                  borderRadius: 10,
                  borderLeft: `3px solid ${color}`,
                }}
              >
                <span
                  style={{
                    color: 'var(--text-secondary)',
                    fontSize: 13,
                    fontWeight: 600,
                    minWidth: 52,
                    flexShrink: 0,
                  }}
                >
                  {period}
                </span>
                <span
                  style={{
                    background: `${color}22`,
                    color,
                    fontSize: 12,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 6,
                    flexShrink: 0,
                  }}
                >
                  {limit}
                </span>
                <span style={{color: 'var(--text-muted)', fontSize: 12, flex: '1 1 120px'}}>
                  {consequence}
                </span>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 14,
              padding: '10px 12px',
              background: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.2)',
              borderRadius: 8,
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
            }}
          >
            <span style={{fontSize: 13, flexShrink: 0}}>⚠</span>
            <span style={{color: '#F59E0B', fontSize: 12, lineHeight: 1.5}}>
              These rules are applied to maintain community quality and encourage responsible
              analysis sharing.
            </span>
          </div>
        </div>
      </div>

      {/* Setup Distribution */}
      <div style={{...CARD, marginBottom: 12}}>
        <span style={SECTION_TITLE}>Setup Distribution</span>
        {totalStatusCount > 0 ? (
          <>
            <div
              style={{
                display: 'flex',
                height: 10,
                borderRadius: 6,
                overflow: 'hidden',
                marginBottom: 14,
                gap: 2,
              }}
            >
              {statusBar.map(({label, count, color}) => (
                <div
                  key={label}
                  style={{
                    flex: count / totalStatusCount,
                    background: color,
                    borderRadius: 4,
                    transition: 'flex 0.3s',
                  }}
                />
              ))}
            </div>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px 16px'}}>
              {statusBar.map(({label, count, color}) => (
                <div key={label} style={{display: 'flex', alignItems: 'center', gap: 6}}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: color,
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{color: 'var(--text-muted)', fontSize: 12}}>{label}</span>
                  <span style={{color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600}}>
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p style={{color: 'var(--text-faint)', fontSize: 13, margin: 0}}>No setups yet.</p>
        )}
      </div>

      {/* Recent Closed Setups */}
      <div style={{...CARD, marginBottom: 12}}>
        <span style={SECTION_TITLE}>Recent Closed Setups</span>
        {metrics.recentClosed.length === 0 ? (
          <p style={{color: 'var(--text-faint)', fontSize: 13, margin: 0}}>No closed setups yet.</p>
        ) : (
          <div style={{display: 'flex', flexDirection: 'column', gap: 0}}>
            {metrics.recentClosed.map((s) => {
              const isWin = s.status === 'success'
              return (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 0',
                    borderBottom: '1px solid var(--divider)',
                  }}
                >
                  <div
                    style={{display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1}}
                  >
                    {s.coin_icon && (
                      <img
                        src={s.coin_icon}
                        alt={s.coin_name}
                        style={{width: 28, height: 28, borderRadius: '50%', flexShrink: 0}}
                      />
                    )}
                    <div style={{minWidth: 0}}>
                      <span
                        style={{
                          color: 'var(--text-primary)',
                          fontSize: 13,
                          fontWeight: 600,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {s.coin_name}
                      </span>
                      <div style={{display: 'flex', gap: 6, marginTop: 2}}>
                        <span
                          style={{
                            background:
                              s.category === 'futures'
                                ? 'rgba(245,158,11,0.12)'
                                : 'rgba(99,102,241,0.12)',
                            color: s.category === 'futures' ? '#F59E0B' : '#818cf8',
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '1px 7px',
                            borderRadius: 20,
                          }}
                        >
                          {s.category}
                        </span>
                        {s.position && (
                          <span
                            style={{
                              background:
                                s.position === 'long'
                                  ? 'rgba(34,197,94,0.1)'
                                  : 'rgba(232,64,64,0.1)',
                              color: s.position === 'long' ? '#22C55E' : '#E84040',
                              fontSize: 11,
                              fontWeight: 600,
                              padding: '1px 7px',
                              borderRadius: 20,
                            }}
                          >
                            {s.position}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{textAlign: 'right'}}>
                    <span
                      style={{
                        color: isWin ? '#22C55E' : '#E84040',
                        fontSize: 13,
                        fontWeight: 700,
                        display: 'block',
                      }}
                    >
                      {isWin ? '✓ Win' : '✗ Stop'}
                    </span>
                    <span style={{color: 'var(--text-muted)', fontSize: 11}}>
                      {(s.realizedR >= 0 ? '+' : '') + s.realizedR.toFixed(2)}R
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ==================== EARNINGS MODEL ==================== */}
      <h2
        style={{color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: '28px 0 14px'}}
      >
        Earnings Model
      </h2>
      <div style={{gap: 12, marginBottom: 12}} className='grid grid-cols-1 lg:grid-cols-2'>
        {/* Earnings Eligibility */}
        <div style={CARD}>
          <span style={SECTION_TITLE}>Earnings Eligibility ({selectedPeriodLabel})</span>
          <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
            {earningsChecks.map(({label, pass, value}) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  background: 'var(--elevated-bg)',
                  borderRadius: 10,
                  borderLeft: `3px solid ${pass ? '#22C55E' : '#E84040'}`,
                }}
              >
                <span style={{fontSize: 16, flexShrink: 0}}>{pass ? '✅' : '❌'}</span>
                <span
                  style={{color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, flex: 1}}
                >
                  {label}
                </span>
                <span
                  style={{
                    color: pass ? '#22C55E' : '#E84040',
                    fontSize: 13,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {value}
                </span>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 14,
              padding: '12px 14px',
              background: isEligible ? 'rgba(34,197,94,0.08)' : 'rgba(232,64,64,0.08)',
              border: `1px solid ${isEligible ? 'rgba(34,197,94,0.2)' : 'rgba(232,64,64,0.2)'}`,
              borderRadius: 10,
              textAlign: 'center',
            }}
          >
            <span
              style={{
                color: isEligible ? '#22C55E' : '#E84040',
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {isEligible ? '$100 Minimum Payment Eligible' : 'Not Yet Eligible'}
            </span>
          </div>
        </div>

        {/* Performance Bonus Calculator */}
        <div style={CARD}>
          <span style={SECTION_TITLE}>Performance Bonus Calculator</span>
          <div style={{display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14}}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 14px',
                background: 'var(--elevated-bg)',
                borderRadius: 10,
              }}
            >
              <span style={{color: 'var(--text-muted)', fontSize: 12}}>Selected Period Net R</span>
              <span style={{color: 'var(--text-primary)', fontSize: 14, fontWeight: 700}}>
                {periodNetR >= 0 ? '+' : ''}
                {periodNetR.toFixed(2)}R
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 14px',
                background: 'var(--elevated-bg)',
                borderRadius: 10,
              }}
            >
              <span style={{color: 'var(--text-muted)', fontSize: 12}}>$30 x R = A</span>
              <span style={{color: 'var(--text-primary)', fontSize: 14, fontWeight: 700}}>
                ${bonusA.toFixed(0)}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 14px',
                background: 'var(--elevated-bg)',
                borderRadius: 10,
              }}
            >
              <span style={{color: 'var(--text-muted)', fontSize: 12}}>
                WR Multiplier ({periodWR.toFixed(0)}%)
              </span>
              <span style={{color: '#818cf8', fontSize: 14, fontWeight: 700}}>
                x{wrMultiplier.toFixed(2)}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 14px',
                background: 'var(--elevated-bg)',
                borderRadius: 10,
              }}
            >
              <span style={{color: 'var(--text-muted)', fontSize: 12}}>Minimum Payment</span>
              <span
                style={{
                  color: minimumPayment > 0 ? '#22C55E' : 'var(--text-faint)',
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                ${minimumPayment.toFixed(0)}
              </span>
            </div>
          </div>
          <div
            style={{
              padding: '14px',
              background:
                totalPeriodEarnings > 0 ? 'rgba(34,197,94,0.08)' : 'rgba(100,116,139,0.08)',
              border: `1px solid ${
                totalPeriodEarnings > 0 ? 'rgba(34,197,94,0.2)' : 'rgba(100,116,139,0.2)'
              }`,
              borderRadius: 10,
              textAlign: 'center',
              marginBottom: 14,
            }}
          >
            <span
              style={{color: 'var(--text-muted)', fontSize: 11, display: 'block', marginBottom: 4}}
            >
              Estimated Period Payment
            </span>
            <span
              style={{
                color: totalPeriodEarnings > 0 ? '#22C55E' : 'var(--text-faint)',
                fontSize: 22,
                fontWeight: 700,
              }}
            >
              ${totalPeriodEarnings.toFixed(0)}
            </span>
            <span
              style={{color: 'var(--text-faint)', fontSize: 11, display: 'block', marginTop: 4}}
            >
              ${minimumPayment.toFixed(0)} minimum + ${estimatedBonus.toFixed(0)} performance bonus
            </span>
          </div>
          <details
            style={{
              border: '1px solid var(--card-border)',
              borderRadius: 10,
              background: 'var(--elevated-bg)',
              padding: 0,
              overflow: 'hidden',
            }}
          >
            <summary
              style={{
                cursor: 'pointer',
                listStyle: 'none',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                color: 'var(--text-secondary)',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                <span style={{color: '#818cf8', fontSize: 14}}>📋</span>
                Win Rate Multiplier Table
              </span>
              <span
                aria-hidden
                className='wr-mult-chevron'
                style={{
                  color: 'var(--text-muted)',
                  fontSize: 11,
                  transition: 'transform 0.18s ease',
                  display: 'inline-block',
                }}
              >
                ▾
              </span>
            </summary>
            <style>{`
              details[open] > summary .wr-mult-chevron { transform: rotate(180deg); }
              details > summary::-webkit-details-marker { display: none; }
            `}</style>
            <div style={{display: 'flex', flexDirection: 'column', gap: 4, padding: '0 10px 10px'}}>
              {bonusMultiplierTable.map(({range, mult}) => {
                const isActive = wrMultiplier.toFixed(2) === Number(mult).toFixed(2)
                return (
                  <div
                    key={range}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '6px 10px',
                      borderRadius: 6,
                      fontSize: 12,
                      background: isActive ? 'rgba(129,140,248,0.1)' : 'transparent',
                      border: isActive
                        ? '1px solid rgba(129,140,248,0.25)'
                        : '1px solid transparent',
                    }}
                  >
                    <span style={{color: isActive ? '#818cf8' : 'var(--text-muted)'}}>{range}</span>
                    <span
                      style={{
                        color: isActive ? '#818cf8' : 'var(--text-secondary)',
                        fontWeight: 600,
                      }}
                    >
                      x{mult}
                    </span>
                  </div>
                )
              })}
            </div>
          </details>
        </div>
      </div>

      {/* ==================== REWARDS & GROWTH ==================== */}
      <div style={{display: 'flex', alignItems: 'center', gap: 10, margin: '28px 0 14px'}}>
        <h2 style={{color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: 0}}>
          Rewards &amp; Growth
        </h2>
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#818cf8',
            background: '#818cf818',
            border: '1px solid #818cf844',
            borderRadius: 999,
            padding: '3px 9px',
          }}
        >
          Estimate
        </span>
      </div>
      <div style={{gap: 12, marginBottom: 12}} className='grid grid-cols-1 lg:grid-cols-3'>
        {/* Monthly Shares */}
        <div style={CARD}>
          <span style={SECTION_TITLE}>Monthly Shares ({selectedPeriodLabel})</span>
          <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between'}}>
            <div style={{display: 'flex', alignItems: 'baseline', gap: 6}}>
              <span style={{color: 'var(--text-primary)', fontSize: 28, fontWeight: 800}}>
                {sharesCount}
              </span>
              <span style={{color: 'var(--text-muted)', fontSize: 12}}>/ {SHARE_MIN} min</span>
            </div>
            <span
              style={{
                color: shareMultiplier > 0 ? '#818cf8' : 'var(--text-faint)',
                fontSize: 16,
                fontWeight: 800,
              }}
            >
              +{shareMultiplier.toFixed(2)}x
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: 'var(--elevated-bg)',
              borderRadius: 4,
              overflow: 'hidden',
              margin: '12px 0 8px',
            }}
          >
            <div
              style={{
                width: `${Math.min((shareMultiplier / SHARE_CAP) * 100, 100)}%`,
                height: '100%',
                background: '#818cf8',
                borderRadius: 4,
                transition: 'width 0.4s',
              }}
            />
          </div>
          <span style={{color: 'var(--text-muted)', fontSize: 11}}>
            {sharesCount < SHARE_MIN
              ? `Publish ${SHARE_MIN - sharesCount} more to activate the multiplier`
              : shareMultiplier >= SHARE_CAP
              ? `Max reached (+${SHARE_CAP.toFixed(2)}x)`
              : `+${SHARE_PER.toFixed(2)}x per share, up to +${SHARE_CAP.toFixed(2)}x`}
          </span>
        </div>

        {/* Active Referrals */}
        <div style={CARD}>
          <span style={SECTION_TITLE}>Active Referrals ({selectedPeriodLabel})</span>
          <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between'}}>
            <div style={{display: 'flex', alignItems: 'baseline', gap: 6}}>
              <span style={{color: 'var(--text-primary)', fontSize: 28, fontWeight: 800}}>
                {activeReferrals}
              </span>
              <span style={{color: 'var(--text-muted)', fontSize: 12}}>/ {REFERRAL_MIN} min</span>
            </div>
            <span
              style={{
                color: referralMultiplier > 0 ? '#22C55E' : 'var(--text-faint)',
                fontSize: 16,
                fontWeight: 800,
              }}
            >
              +{referralMultiplier.toFixed(2)}x
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: 'var(--elevated-bg)',
              borderRadius: 4,
              overflow: 'hidden',
              margin: '12px 0 8px',
            }}
          >
            <div
              style={{
                width: `${Math.min((referralMultiplier / REFERRAL_CAP) * 100, 100)}%`,
                height: '100%',
                background: '#22C55E',
                borderRadius: 4,
                transition: 'width 0.4s',
              }}
            />
          </div>
          <span style={{color: 'var(--text-muted)', fontSize: 11}}>
            {activeReferrals < REFERRAL_MIN
              ? `${REFERRAL_MIN - activeReferrals} more active referrals to activate`
              : referralMultiplier >= REFERRAL_CAP
              ? `Max reached (+${REFERRAL_CAP.toFixed(2)}x)`
              : `+${REFERRAL_PER.toFixed(2)}x per active referral, up to +${REFERRAL_CAP.toFixed(
                  2
                )}x`}
          </span>
        </div>

        {/* Engagement Points */}
        <div style={CARD}>
          <span style={SECTION_TITLE}>Engagement Points ({selectedPeriodLabel})</span>
          <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between'}}>
            <div style={{display: 'flex', alignItems: 'baseline', gap: 6}}>
              <span style={{color: 'var(--text-primary)', fontSize: 28, fontWeight: 800}}>
                {engagementBreakdown.total}
              </span>
              <span style={{color: 'var(--text-muted)', fontSize: 12}}>pts</span>
            </div>
            <span
              style={{
                color: engagementBonus > 0 ? '#F59E0B' : 'var(--text-faint)',
                fontSize: 16,
                fontWeight: 800,
              }}
            >
              {formatUSD(engagementBonus)}
            </span>
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12}}>
            {[
              {label: 'Active days', value: engagementBreakdown.activeDays},
              {label: 'Shares', value: engagementBreakdown.shares},
              {label: 'Take profits', value: engagementBreakdown.takeProfits},
              {label: '7-day streaks', value: engagementBreakdown.streaks7},
            ].map(({label, value}) => (
              <div
                key={label}
                style={{display: 'flex', justifyContent: 'space-between', fontSize: 11}}
              >
                <span style={{color: 'var(--text-muted)'}}>{label}</span>
                <span style={{color: 'var(--text-secondary)', fontWeight: 600}}>{value} pts</span>
              </div>
            ))}
            <div style={{display: 'flex', justifyContent: 'space-between', fontSize: 11}}>
              <span style={{color: 'var(--text-faint)'}}>Follower interactions</span>
              <span style={{color: 'var(--text-faint)'}}>soon</span>
            </div>
          </div>
        </div>
      </div>

      {/* Official monthly payout from the server, or the local estimate off-month. */}
      <div style={{...CARD, marginBottom: 12}}>
        <span style={SECTION_TITLE}>Estimated Total Payout — {selectedPeriodLabel}</span>
        {currentPayout?.calculation ? (
          <div style={{display: 'grid', gap: 10}}>
            <div className='grid grid-cols-1 sm:grid-cols-2' style={{gap: 8}}>
              <div style={{padding: '10px 14px', background: 'var(--elevated-bg)', borderRadius: 10}}>
                <span style={{color: 'var(--text-muted)', fontSize: 12}}>With consistency</span>
                <div style={{color: '#22C55E', fontSize: 18, fontWeight: 800}}>
                  {formatUSD(currentPayout.total_usd)}
                </div>
              </div>
              <div style={{padding: '10px 14px', background: 'var(--elevated-bg)', borderRadius: 10}}>
                <span style={{color: 'var(--text-muted)', fontSize: 12}}>Without consistency gates</span>
                <div style={{color: 'var(--text-primary)', fontSize: 18, fontWeight: 800}}>
                  {formatUSD(currentPayout.payout_without_consistency_usd ?? 0)}
                </div>
              </div>
            </div>
            <p style={{color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.55, margin: 0}}>
              {currentPayout.calculation}
            </p>
            {currentPayout.failure_reason ? (
              <p style={{color: '#E84040', fontSize: 12, margin: 0}}>{currentPayout.failure_reason}</p>
            ) : null}
          </div>
        ) : isEligible ? (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-2' style={{gap: 8, marginBottom: 12}}>
              {[
                {label: 'Base payment', value: formatUSD(rewardPayout.base), color: '#22C55E'},
                {
                  label: `Effective multiplier (WR ${wrMultiplier.toFixed(
                    2
                  )} + share ${shareMultiplier.toFixed(2)} + ref ${referralMultiplier.toFixed(2)})`,
                  value: `x${rewardPayout.effectiveMultiplier.toFixed(2)}`,
                  color: '#818cf8',
                },
                {
                  label: 'Performance bonus (30 x NetR x multiplier)',
                  value: formatUSD(rewardPayout.performanceBonus),
                  color: 'var(--text-primary)',
                },
                {
                  label: 'Engagement bonus',
                  value: formatUSD(rewardPayout.engagementBonus),
                  color: '#F59E0B',
                },
              ].map(({label, value, color}) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    background: 'var(--elevated-bg)',
                    borderRadius: 10,
                  }}
                >
                  <span style={{color: 'var(--text-muted)', fontSize: 12}}>{label}</span>
                  <span style={{color, fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap'}}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
            <div
              style={{
                padding: '14px',
                background: 'rgba(34,197,94,0.08)',
                border: '1px solid rgba(34,197,94,0.2)',
                borderRadius: 10,
                textAlign: 'center',
              }}
            >
              <span
                style={{
                  color: 'var(--text-muted)',
                  fontSize: 11,
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                Estimated Total (base + performance bonus + engagement)
              </span>
              <span style={{color: '#22C55E', fontSize: 24, fontWeight: 800}}>
                {formatUSD(rewardPayout.total)}
              </span>
            </div>
          </>
        ) : (
          <div
            style={{
              padding: '14px',
              background: 'rgba(232,64,64,0.08)',
              border: '1px solid rgba(232,64,64,0.2)',
              borderRadius: 10,
              textAlign: 'center',
              color: '#E84040',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            Not eligible this period — rewards apply once earnings &amp; consistency rules pass.
          </div>
        )}
        <p style={{color: 'var(--text-faint)', fontSize: 11, margin: '10px 0 0', lineHeight: 1.5}}>
          Estimate based on your activity. Share &amp; referral multipliers stack on top of the
          win-rate multiplier; engagement points convert to a capped bonus. Final payouts are
          confirmed by the team.
        </p>
      </div>

      {/* ==================== SOCIAL MEDIA RANK ==================== */}
      {socialRankPeriod ? (
        <>
          <div style={{display: 'flex', alignItems: 'center', gap: 10, margin: '28px 0 14px'}}>
            <h2 style={{color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: 0}}>
              Social Media Rank
            </h2>
            {socialScore?.isFinalized ? (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#22C55E',
                  background: '#22C55E18',
                  border: '1px solid #22C55E44',
                  borderRadius: 999,
                  padding: '3px 9px',
                }}
              >
                Finalized
              </span>
            ) : (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#38bdf8',
                  background: '#38bdf818',
                  border: '1px solid #38bdf844',
                  borderRadius: 999,
                  padding: '3px 9px',
                }}
              >
                Live
              </span>
            )}
          </div>
          <div style={{gap: 12, marginBottom: 12}} className='grid grid-cols-1 lg:grid-cols-2'>
            <div style={CARD}>
              <span style={SECTION_TITLE}>Social Rank — {selectedPeriodLabel}</span>
              {isSocialScoreLoading ? (
                <span style={{color: 'var(--text-muted)', fontSize: 13}}>Loading...</span>
              ) : (
                <>
                  <div style={{display: 'flex', alignItems: 'baseline', gap: 8}}>
                    <span style={{color: 'var(--text-primary)', fontSize: 32, fontWeight: 800}}>
                      {socialScore?.socialRank != null ? `#${socialScore.socialRank}` : '—'}
                    </span>
                    <span style={{color: 'var(--text-muted)', fontSize: 13}}>
                      out of {socialScore?.traderCount ?? 0} traders
                    </span>
                  </div>
                  <p
                    style={{
                      color: 'var(--text-muted)',
                      fontSize: 11,
                      margin: '10px 0 0',
                      lineHeight: 1.5,
                    }}
                  >
                    Ranked by verified BottomUP-related social activity, normalized by platform and
                    follower size.
                  </p>
                </>
              )}
            </div>
            <div style={CARD}>
              <span style={SECTION_TITLE}>Social Score — {selectedPeriodLabel}</span>
              {isSocialScoreLoading ? (
                <span style={{color: 'var(--text-muted)', fontSize: 13}}>Loading...</span>
              ) : (
                <>
                  <div style={{display: 'flex', alignItems: 'baseline', gap: 6}}>
                    <span style={{color: '#38bdf8', fontSize: 32, fontWeight: 800}}>
                      {socialScore?.totalSocialScore ?? 0}
                    </span>
                    <span style={{color: 'var(--text-muted)', fontSize: 13}}>/ 300 pts</span>
                  </div>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12}}>
                    {[
                      {label: 'Activity', value: socialScore?.breakdown.baseActivityPoints ?? 0},
                      {
                        label: 'Engagement',
                        value: socialScore?.breakdown.engagementQualityPoints ?? 0,
                      },
                      {label: 'Consistency', value: socialScore?.breakdown.consistencyPoints ?? 0},
                      {
                        label: 'Diversity',
                        value: socialScore?.breakdown.platformDiversityPoints ?? 0,
                      },
                    ].map(({label, value}) => (
                      <span
                        key={label}
                        style={{
                          fontSize: 11,
                          color: 'var(--text-secondary)',
                          background: 'var(--elevated-bg)',
                          borderRadius: 999,
                          padding: '4px 10px',
                        }}
                      >
                        {label}: {value}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <div style={{...CARD, marginBottom: 12}}>
            <span style={SECTION_TITLE}>Social Media Performance — {selectedPeriodLabel}</span>
            {isSocialScoreLoading ? (
              <span style={{color: 'var(--text-muted)', fontSize: 13}}>Loading...</span>
            ) : (
              <>
                <div
                  className='grid grid-cols-2 sm:grid-cols-4'
                  style={{gap: 10, marginBottom: 14}}
                >
                  {[
                    {
                      label: 'Original Posts',
                      value: socialScore?.metrics.originalContentCount ?? 0,
                    },
                    {label: 'Reshares', value: socialScore?.metrics.reshareCount ?? 0},
                    {label: 'Comments', value: socialScore?.metrics.commentCount ?? 0},
                    {
                      label: 'High Engagement',
                      value: socialScore?.metrics.highEngagementContentCount ?? 0,
                    },
                  ].map(({label, value}) => (
                    <div
                      key={label}
                      style={{
                        padding: '10px 12px',
                        background: 'var(--elevated-bg)',
                        borderRadius: 10,
                        textAlign: 'center',
                      }}
                    >
                      <span
                        style={{
                          display: 'block',
                          color: 'var(--text-muted)',
                          fontSize: 10,
                          marginBottom: 4,
                        }}
                      >
                        {label}
                      </span>
                      <span style={{color: 'var(--text-primary)', fontSize: 18, fontWeight: 800}}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
                {(socialScore?.platformBreakdown?.length ?? 0) > 0 ? (
                  <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
                    {socialScore!.platformBreakdown.map((row) => (
                      <div
                        key={row.platform}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          background: 'var(--elevated-bg)',
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                      >
                        <span style={{color: 'var(--text-secondary)', fontWeight: 600}}>
                          {PLATFORM_LABELS[row.platform] ?? row.platform}
                        </span>
                        <span style={{color: 'var(--text-muted)'}}>
                          {row.points} pts · {row.contents} items
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{color: 'var(--text-muted)', fontSize: 12, margin: 0}}>
                    No eligible social content yet. Connect your accounts and share BottomUP-related
                    posts to start scoring.
                  </p>
                )}
                <p
                  style={{
                    color: 'var(--text-faint)',
                    fontSize: 11,
                    margin: '12px 0 0',
                    lineHeight: 1.5,
                  }}
                >
                  {socialScore?.metrics.activeWeekCount ?? 0} active weeks ·{' '}
                  {socialScore?.metrics.activePlatformCount ?? 0} active platforms
                  {(socialScore?.breakdown.penaltyPoints ?? 0) < 0
                    ? ` · ${socialScore!.breakdown.penaltyPoints} penalty pts`
                    : ''}
                </p>
              </>
            )}
          </div>
        </>
      ) : null}

      <div style={{...CARD, marginBottom: 12}}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <div>
            <span style={SECTION_TITLE}>My Eligibility Details</span>
            <p
              style={{
                color: 'var(--text-muted)',
                fontSize: 12,
                margin: '-8px 0 0',
                lineHeight: 1.5,
              }}
            >
              All earning and consistency rules must pass for the selected period payout.
            </p>
          </div>
          <div
            style={{
              alignSelf: 'flex-start',
              borderRadius: 999,
              padding: '8px 12px',
              background: isEligible ? 'rgba(34,197,94,0.1)' : 'rgba(232,64,64,0.1)',
              color: isEligible ? '#22C55E' : '#E84040',
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {isEligible ? 'Eligible' : 'Not Eligible'}
          </div>
        </div>

        {notEligibleReasons.length > 0 ? (
          <div style={{display: 'grid', gap: 8, marginBottom: 16}}>
            {notEligibleReasons.map((check) => (
              <div
                key={check.label}
                style={{
                  border: '1px solid rgba(232,64,64,0.25)',
                  background: 'rgba(232,64,64,0.07)',
                  borderRadius: 10,
                  padding: '10px 12px',
                }}
              >
                <span style={{display: 'block', color: '#E84040', fontSize: 13, fontWeight: 700}}>
                  {check.label}
                </span>
                <span
                  style={{display: 'block', color: 'var(--text-muted)', fontSize: 12, marginTop: 2}}
                >
                  Current value: {check.value}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              border: '1px solid rgba(34,197,94,0.22)',
              background: 'rgba(34,197,94,0.08)',
              borderRadius: 10,
              padding: '10px 12px',
              color: '#22C55E',
              fontSize: 13,
              fontWeight: 700,
              marginBottom: 16,
            }}
          >
            All measurable earning and consistency checks passed.
          </div>
        )}

        <div className='grid grid-cols-1 lg:grid-cols-2' style={{gap: 12}}>
          <div style={{background: 'var(--elevated-bg)', borderRadius: 12, padding: 14}}>
            <span
              style={{
                display: 'block',
                color: 'var(--text-secondary)',
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 10,
              }}
            >
              Daily R Breakdown
            </span>
            {dailyBreakdown.length === 0 ? (
              <p style={{color: 'var(--text-muted)', fontSize: 12, margin: 0}}>
                No closed trades found for this period.
              </p>
            ) : (
              <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                {dailyBreakdown.map((day) => (
                  <div
                    key={day.day}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto auto',
                      gap: 10,
                      alignItems: 'center',
                      padding: '8px 10px',
                      border: '1px solid var(--card-border)',
                      borderRadius: 8,
                    }}
                  >
                    <span style={{color: 'var(--text-primary)', fontSize: 12, fontWeight: 700}}>
                      {day.day}
                    </span>
                    <span style={{color: 'var(--text-muted)', fontSize: 12}}>
                      {day.tradeCount} trade{day.tradeCount !== 1 ? 's' : ''}
                    </span>
                    <span
                      style={{
                        color: day.r >= 0 ? '#22C55E' : '#E84040',
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {day.r >= 0 ? '+' : ''}
                      {day.r.toFixed(2)}R
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{background: 'var(--elevated-bg)', borderRadius: 12, padding: 14}}>
            <span
              style={{
                display: 'block',
                color: 'var(--text-secondary)',
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 10,
              }}
            >
              Closed Trades ({selectedPeriodLabel})
            </span>
            {closedPeriodDetails.length === 0 ? (
              <p style={{color: 'var(--text-muted)', fontSize: 12, margin: 0}}>
                No closed trades found for this period.
              </p>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  maxHeight: 360,
                  overflow: 'auto',
                }}
              >
                {closedPeriodDetails.map((trade: any, idx: number) => {
                  const rValue = Number(trade.r ?? trade.pnl ?? 0)
                  const dateValue =
                    trade.target_date || trade.close_date || trade.updated_at || trade.created_at
                  return (
                    <div
                      key={`${trade.coin_name ?? 'trade'}-${dateValue ?? idx}-${idx}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto',
                        gap: 10,
                        alignItems: 'center',
                        padding: '8px 10px',
                        border: '1px solid var(--card-border)',
                        borderRadius: 8,
                      }}
                    >
                      <div>
                        <span
                          style={{
                            display: 'block',
                            color: 'var(--text-primary)',
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {trade.coin_name || '-'}
                        </span>
                        <span style={{display: 'block', color: 'var(--text-faint)', fontSize: 11}}>
                          {trade.position || '-'} · {String(trade.status || '-')}
                        </span>
                      </div>
                      <span
                        style={{
                          color: rValue >= 0 ? '#22C55E' : '#E84040',
                          fontSize: 12,
                          fontWeight: 800,
                        }}
                      >
                        {rValue >= 0 ? '+' : ''}
                        {rValue.toFixed(2)}R
                      </span>
                      <span style={{color: 'var(--text-muted)', fontSize: 11}}>
                        {formatTradeDate(dateValue)}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
            {closedPeriod.length > 50 ? (
              <p style={{color: 'var(--text-faint)', fontSize: 11, margin: '10px 0 0'}}>
                Showing latest 50 of {closedPeriod.length} trades.
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {SHOW_PAYOUT_SECTION && (
        <>
          {/* ==================== MONTHLY PAYOUT ==================== */}
          <h2
            style={{
              color: 'var(--text-primary)',
              fontSize: 16,
              fontWeight: 700,
              margin: '28px 0 14px',
            }}
          >
            Payout
          </h2>
          <div style={{...CARD, marginBottom: 12}}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
                flexWrap: 'wrap',
                marginBottom: 16,
              }}
            >
              <div>
                <span style={SECTION_TITLE}>Withdraw {selectedPeriodLabel} Earnings</span>
                <p
                  style={{
                    color: 'var(--text-muted)',
                    fontSize: 12,
                    margin: '-8px 0 0',
                    lineHeight: 1.5,
                  }}
                >
                  Save a payout destination once. Eligible monthly payouts are sent automatically
                  from the company OKX treasury on the 20th.
                </p>
              </div>
              <div style={{textAlign: 'right'}}>
                <span style={{display: 'block', color: 'var(--text-muted)', fontSize: 11}}>
                  Estimated Period Payment
                </span>
                <span
                  style={{
                    display: 'block',
                    color: totalPeriodEarnings > 0 ? '#22C55E' : 'var(--text-faint)',
                    fontSize: 24,
                    fontWeight: 800,
                  }}
                >
                  {formatUSD(totalPeriodEarnings)}
                </span>
              </div>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-3' style={{gap: 12, marginBottom: 16}}>
              <div style={{background: 'var(--elevated-bg)', borderRadius: 12, padding: 14}}>
                <span
                  style={{
                    display: 'block',
                    color: 'var(--text-muted)',
                    fontSize: 11,
                    marginBottom: 6,
                  }}
                >
                  Scheduled Amount
                </span>
                <span style={{color: 'var(--text-primary)', fontSize: 18, fontWeight: 700}}>
                  {formatUSD(payoutUsd)}
                </span>
                <span
                  style={{display: 'block', color: 'var(--text-faint)', fontSize: 11, marginTop: 4}}
                >
                  Status: {payoutStatusLabel}
                </span>
              </div>
              <div style={{background: 'var(--elevated-bg)', borderRadius: 12, padding: 14}}>
                <span
                  style={{
                    display: 'block',
                    color: 'var(--text-muted)',
                    fontSize: 11,
                    marginBottom: 6,
                  }}
                >
                  Payment Method
                </span>
                <select
                  value={selectedPayoutMethod}
                  onChange={(e) => setSelectedPayoutMethod(e.target.value as PayoutMethod)}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 8,
                    padding: '8px 10px',
                    fontSize: 13,
                    outline: 'none',
                  }}
                >
                  {PAYOUT_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
                <span
                  style={{display: 'block', color: 'var(--text-faint)', fontSize: 11, marginTop: 6}}
                >
                  {
                    PAYOUT_METHODS.find((method) => method.value === selectedPayoutMethod)
                      ?.description
                  }
                </span>
              </div>
              <div style={{background: 'var(--elevated-bg)', borderRadius: 12, padding: 14}}>
                <span
                  style={{
                    display: 'block',
                    color: 'var(--text-muted)',
                    fontSize: 11,
                    marginBottom: 6,
                  }}
                >
                  Backend Calculation
                </span>
                <input
                  type='number'
                  min='0'
                  value={suggestedPayoutUsd}
                  placeholder={String(suggestedPayoutUsd)}
                  readOnly
                  style={{
                    width: '100%',
                    background: 'transparent',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 8,
                    padding: '8px 10px',
                    fontSize: 13,
                    outline: 'none',
                  }}
                />
                <span
                  style={{display: 'block', color: 'var(--text-faint)', fontSize: 11, marginTop: 6}}
                >
                  Final payout is locked by backend at processing time
                </span>
              </div>
            </div>

            <div
              style={{
                border: '1px solid var(--card-border)',
                borderRadius: 12,
                padding: 14,
                marginBottom: 16,
              }}
            >
              <span
                style={{
                  display: 'block',
                  color: 'var(--text-secondary)',
                  fontSize: 13,
                  fontWeight: 700,
                  marginBottom: 8,
                }}
              >
                Selected Destination
              </span>
              <select
                value={selectedPayoutWalletId}
                onChange={(e) => setSelectedPayoutWalletId(e.target.value)}
                disabled={isWalletsLoading || methodWallets.length === 0}
                style={{
                  width: '100%',
                  background: 'var(--elevated-bg)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--card-border)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 13,
                  outline: 'none',
                }}
              >
                <option value=''>
                  {isWalletsLoading
                    ? 'Loading destinations...'
                    : `No saved ${
                        PAYOUT_METHODS.find((method) => method.value === selectedPayoutMethod)
                          ?.label
                      } destination`}
                </option>
                {methodWallets.map((wallet) => (
                  <option key={wallet.id} value={wallet.id}>
                    {wallet.label || wallet.chain} - {shortAddress(wallet.address)}
                  </option>
                ))}
              </select>
              <span
                style={{display: 'block', color: 'var(--text-faint)', fontSize: 11, marginTop: 6}}
              >
                {selectedWallet
                  ? `${selectedWallet.label || selectedWallet.chain} selected`
                  : 'Save or connect a destination below to continue.'}
              </span>
            </div>

            <div style={{marginBottom: 16}}>
              {selectedPayoutMethod === 'metamask' && (
                <div
                  style={{border: '1px solid var(--card-border)', borderRadius: 12, padding: 14}}
                >
                  <span
                    style={{
                      display: 'block',
                      color: 'var(--text-secondary)',
                      fontSize: 13,
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    Connect MetaMask
                  </span>
                  <p
                    style={{
                      color: 'var(--text-muted)',
                      fontSize: 12,
                      lineHeight: 1.5,
                      margin: '0 0 12px',
                    }}
                  >
                    Use your browser wallet as the payout destination. We ask for a signature only;
                    no transaction is sent.
                  </p>
                  <button
                    type='button'
                    onClick={handleMetaMaskConnect}
                    disabled={connectWalletMutation.isLoading}
                    style={{
                      width: '100%',
                      border: '0',
                      borderRadius: 10,
                      padding: '10px 12px',
                      background: '#F59E0B',
                      color: '#111827',
                      fontWeight: 800,
                      cursor: connectWalletMutation.isLoading ? 'not-allowed' : 'pointer',
                      opacity: connectWalletMutation.isLoading ? 0.7 : 1,
                    }}
                  >
                    {connectWalletMutation.isLoading ? 'Connecting...' : 'Connect MetaMask'}
                  </button>
                </div>
              )}

              {selectedPayoutMethod === 'okx' && (
                <div
                  style={{border: '1px solid var(--card-border)', borderRadius: 12, padding: 14}}
                >
                  <span
                    style={{
                      display: 'block',
                      color: 'var(--text-secondary)',
                      fontSize: 13,
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    OKX UID
                  </span>
                  <p
                    style={{
                      color: 'var(--text-muted)',
                      fontSize: 12,
                      lineHeight: 1.5,
                      margin: '0 0 12px',
                    }}
                  >
                    Enter your OKX UID. This UID will be saved as your payout destination.
                  </p>
                  <div style={{display: 'flex', gap: 8}}>
                    <input
                      value={okxUid}
                      onChange={(e) => setOkxUid(e.target.value)}
                      placeholder='OKX UID'
                      inputMode='numeric'
                      style={{
                        flex: 1,
                        minWidth: 0,
                        background: 'var(--elevated-bg)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--card-border)',
                        borderRadius: 10,
                        padding: '10px 12px',
                        fontSize: 13,
                        outline: 'none',
                      }}
                    />
                    <button
                      type='button'
                      onClick={handleConnectOkxUid}
                      disabled={connectWalletMutation.isLoading}
                      style={{
                        border: '0',
                        borderRadius: 10,
                        padding: '10px 12px',
                        background: '#111827',
                        color: '#f8fafc',
                        fontWeight: 800,
                        cursor: connectWalletMutation.isLoading ? 'not-allowed' : 'pointer',
                        opacity: connectWalletMutation.isLoading ? 0.7 : 1,
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}

              {selectedPayoutMethod === 'trc20' && (
                <div
                  style={{border: '1px solid var(--card-border)', borderRadius: 12, padding: 14}}
                >
                  <span
                    style={{
                      display: 'block',
                      color: 'var(--text-secondary)',
                      fontSize: 13,
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    TRC-20 Address
                  </span>
                  <p
                    style={{
                      color: 'var(--text-muted)',
                      fontSize: 12,
                      lineHeight: 1.5,
                      margin: '0 0 12px',
                    }}
                  >
                    Enter a Tron network USDT address. It must start with T and will be saved as a
                    payout wallet.
                  </p>
                  <div style={{display: 'flex', gap: 8}}>
                    <input
                      value={trc20Address}
                      onChange={(e) => setTrc20Address(e.target.value)}
                      placeholder='T...'
                      style={{
                        flex: 1,
                        minWidth: 0,
                        background: 'var(--elevated-bg)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--card-border)',
                        borderRadius: 10,
                        padding: '10px 12px',
                        fontSize: 13,
                        outline: 'none',
                      }}
                    />
                    <button
                      type='button'
                      onClick={handleConnectTrc20}
                      disabled={connectWalletMutation.isLoading}
                      style={{
                        border: '0',
                        borderRadius: 10,
                        padding: '10px 12px',
                        background: '#22C55E',
                        color: '#07120b',
                        fontWeight: 800,
                        cursor: connectWalletMutation.isLoading ? 'not-allowed' : 'pointer',
                        opacity: connectWalletMutation.isLoading ? 0.7 : 1,
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>

            {payoutMessage && (
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  marginBottom: 14,
                  background:
                    payoutMessage.type === 'success'
                      ? 'rgba(34,197,94,0.08)'
                      : 'rgba(232,64,64,0.08)',
                  border: `1px solid ${
                    payoutMessage.type === 'success'
                      ? 'rgba(34,197,94,0.22)'
                      : 'rgba(232,64,64,0.22)'
                  }`,
                  color: payoutMessage.type === 'success' ? '#22C55E' : '#E84040',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                {payoutMessage.text}
              </div>
            )}

            {(!isCurrentPayoutPeriod ||
              !isEligible ||
              totalPeriodEarnings <= 0 ||
              (isCurrentPayoutPeriod &&
                (currentPayout?.status === 'manual_review' ||
                  currentPayout?.status === 'failed'))) && (
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  marginBottom: 14,
                  background: 'rgba(245,158,11,0.08)',
                  border: '1px solid rgba(245,158,11,0.22)',
                  color: '#F59E0B',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                {!isCurrentPayoutPeriod
                  ? 'This is a historical period. Use This Month to schedule the next automatic payout.'
                  : !isEligible || totalPeriodEarnings <= 0
                  ? 'Selected period is not eligible for payout yet.'
                  : currentPayout?.failure_reason || 'This payout needs admin review.'}
              </div>
            )}

            <div
              style={{
                padding: '10px 12px',
                borderRadius: 10,
                marginBottom: 14,
                background: 'rgba(129,140,248,0.08)',
                border: '1px solid rgba(129,140,248,0.2)',
                color: '#818cf8',
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              Bonuses are paid automatically on the 20th of each month. Changing the destination
              updates the next automatic payout; already processed payouts keep their original
              destination snapshot.
            </div>

            <button
              type='button'
              onClick={handleRequestPayout}
              disabled={!canRequestPayout}
              style={{
                width: '100%',
                border: '0',
                borderRadius: 12,
                padding: '13px 16px',
                background: canRequestPayout
                  ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)'
                  : 'rgba(100,116,139,0.22)',
                color: canRequestPayout ? '#03110a' : 'var(--text-faint)',
                fontWeight: 900,
                cursor: canRequestPayout ? 'pointer' : 'not-allowed',
              }}
            >
              {canRequestPayout
                ? `Auto Payout Scheduled: ${formatUSD(payoutUsd)}`
                : isCurrentPayoutPeriod
                ? 'Save Destination to Schedule Payout'
                : 'Historical Period - Scheduling Uses This Month'}
            </button>
          </div>
        </>
      )}

      {/* ==================== TRADING RULES ==================== */}
      <h2
        style={{color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: '28px 0 14px'}}
      >
        Trading Rules
      </h2>
      <div style={{...CARD, marginBottom: 12}}>
        <span style={SECTION_TITLE}>Rules Overview</span>
        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
          {tradingRules.map(({label, desc, icon}) => (
            <div
              key={label}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '12px 14px',
                background: 'var(--elevated-bg)',
                borderRadius: 10,
              }}
            >
              <span style={{fontSize: 18, flexShrink: 0, lineHeight: 1.2}}>{icon}</span>
              <div style={{flex: 1}}>
                <span
                  style={{
                    color: 'var(--text-primary)',
                    fontSize: 13,
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: 2,
                  }}
                >
                  {label}
                </span>
                <span style={{color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.4}}>
                  {desc}
                </span>
              </div>
              <span
                style={{
                  background: 'rgba(99,102,241,0.1)',
                  color: '#818cf8',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: 20,
                  flexShrink: 0,
                  alignSelf: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Enforced
              </span>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 14,
            padding: '10px 12px',
            background: 'rgba(99,102,241,0.06)',
            border: '1px solid rgba(99,102,241,0.15)',
            borderRadius: 8,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
          }}
        >
          <span style={{fontSize: 13, flexShrink: 0}}>ℹ</span>
          <span style={{color: '#818cf8', fontSize: 12, lineHeight: 1.5}}>
            Trading rules are enforced server-side. Violations are automatically detected and may
            result in trade rejection or account warnings.
          </span>
        </div>
      </div>

      {/* ==================== CONSISTENCY RULES ==================== */}
      <h2
        style={{color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: '28px 0 14px'}}
      >
        Consistency Rules ({selectedPeriodLabel})
      </h2>
      <div style={CARD}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <span style={{...SECTION_TITLE, marginBottom: 0}}>Checklist</span>
          <span style={{color: 'var(--text-faint)', fontSize: 11}}>
            Based on {consistencyTradeCount} closed trade{consistencyTradeCount !== 1 ? 's' : ''}{' '}
            across {uniqueTradingDays} day{uniqueTradingDays !== 1 ? 's' : ''}
          </span>
        </div>
        <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
          {consistencyChecks.map(({label, pass, value}) => {
            const isPending = pass === null
            const borderColor = isPending ? '#64748b' : pass ? '#22C55E' : '#E84040'
            const icon = isPending ? 'ℹ️' : pass ? '✅' : '❌'
            return (
              <div
                key={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  background: 'var(--elevated-bg)',
                  borderRadius: 10,
                  borderLeft: `3px solid ${borderColor}`,
                }}
              >
                <span style={{fontSize: 16, flexShrink: 0}}>{icon}</span>
                <span
                  style={{color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, flex: 1}}
                >
                  {label}
                </span>
                <span
                  style={{
                    color: isPending ? '#64748b' : pass ? '#22C55E' : '#E84040',
                    fontSize: 13,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {value}
                </span>
              </div>
            )
          })}
        </div>
        <div
          style={{
            marginTop: 14,
            padding: '10px 12px',
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.2)',
            borderRadius: 8,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
          }}
        >
          <span style={{fontSize: 13, flexShrink: 0}}>⚠</span>
          <span style={{color: '#F59E0B', fontSize: 12, lineHeight: 1.5}}>
            All consistency rules must be met by the end of the month for bonus eligibility. Trade
            duration is validated server-side.
          </span>
        </div>
      </div>

      {/* ==================== SUPPORT ==================== */}
      <h2
        style={{color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: '28px 0 14px'}}
      >
        Support and Suggestions
      </h2>
      <div style={CARD}>
        <p style={{color: 'var(--text-muted)', fontSize: 13, margin: 0, lineHeight: 1.6}}>
          For any issues or needs related to operations, contact{' '}
          <a href='mailto:cem@bottomup.app' style={{color: '#818cf8', fontWeight: 700}}>
            cem@bottomup.app
          </a>
          .
        </p>
      </div>
    </div>
  )
}
